<script setup lang="ts">
import { Download, Eye, FileText } from 'lucide-vue-next';
import { computed } from 'vue';

interface Props {
  document?: null | {
    filename: string;
    mime_type?: string;
    url_download: string;
    url_preview: string;
  };
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  document: undefined,
  isLoading: false,
});

const shouldShowSkeleton = computed(() => props.isLoading || !props.document);

const handlePreview = () => {
  if (props.document?.url_preview) {
    window.open(props.document.url_preview, '_blank', 'noopener,noreferrer');
  }
};

const handleDownload = () => {
  if (props.document?.url_download) {
    window.open(props.document.url_download, '_blank', 'noopener,noreferrer');
  }
};
</script>

<template>
  <div class="document-viewer">
    <div class="document-item">
      <div class="document-icon">
        <FileText v-if="!shouldShowSkeleton" class="icon" />
        <span v-else class="skeleton skeleton-icon" />
      </div>
      <div class="document-info">
        <span v-if="!shouldShowSkeleton" class="document-filename">{{ document?.filename }}</span>
        <span v-else class="skeleton skeleton-filename" />
      </div>
      <div class="document-actions">
        <template v-if="!shouldShowSkeleton">
          <button
            v-if="document?.url_preview"
            type="button"
            class="btn btn-sm btn-outline-primary"
            title="Visualizar"
            @click="handlePreview"
          >
            <Eye class="icon icon-sm" />
          </button>
          <button
            v-if="document?.url_download"
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="Baixar"
            @click="handleDownload"
          >
            <Download class="icon icon-sm" />
          </button>
        </template>
        <template v-else>
          <span class="skeleton skeleton-button" />
          <span class="skeleton skeleton-button" />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.document-viewer {
  .document-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    background: #f8f9fa;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
  }

  .document-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: white;
    border-radius: 0.375rem;
    color: #64748b;

    .icon {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  .document-info {
    flex: 1;
    min-width: 0;

    .document-filename {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      color: #0f172a;
      word-break: break-word;
    }
  }

  .document-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.375rem;

      .icon {
        margin: 0;
      }
    }
  }
}

.skeleton {
  display: inline-block;
  border-radius: 0.375rem;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
}

.skeleton-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.skeleton-filename {
  width: 100%;
  height: 1rem;
  max-width: 200px;
}

.skeleton-button {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
